import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  DollarSign, Users, FileText, TrendingUp, AlertTriangle,
  ArrowRight, CheckCircle, Plus, MapPin, X,
  Percent, Receipt, BarChart3, Calendar, MessageSquare,
  Settings, Map, UserPlus, Edit3, Trash2, Trophy, Award, Medal,
  ThumbsUp, Star, Phone, Mail, User, Globe, MessageCircle, Clock, Send, Loader
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, isWithinInterval, startOfWeek, addDays, isBefore, isToday } from 'date-fns';
import { sendWeeklyRecap } from '../utils/emailApi';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import { db } from '../firebase';
import { formatCurrency, formatFullName } from '../utils/helpers';
import { OFFICE } from '../utils/jobData';
import { ReviewAlerts } from '../components/PerformanceReview';
import './Dashboard.css';

// ===== ADMIN NAME MAP =====
const ADMIN_NAMES = {
  'sarah@twosisterscleaningco.com': 'Sarah',
  'sarah.keel@twosisterscleaningco.com': 'Sarah',
  'elaine@twosisterscleaningco.com': 'Elaine',
  'sam@twosisterscleaningco.com': 'Sam',
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function annualMultiplier(freq) {
  switch (freq) {
    case 'weekly': return 52;
    case 'biweekly': return 26;
    case 'monthly': return 12;
    default: return 0;
  }
}

// ===== SPARKBAR MINI CHART =====
function SparkBar({ data, height = 100 }) {
  const max = Math.max(...data.map(d => d.amount), 1);
  return (
    <div className="dash-sparkbar" style={{ height }}>
      {data.map((d, i) => {
        const h = (d.amount / max) * height;
        const isLast = i === data.length - 1;
        return (
          <div key={i} className="dash-sparkbar-col">
            <span className={`dash-sparkbar-val ${isLast ? 'active' : ''}`}>
              {formatCurrency(d.amount).replace('.00', '')}
            </span>
            <div className={`dash-sparkbar-bar ${isLast ? 'active' : ''}`} style={{ height: h }} />
            <span className="dash-sparkbar-label">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ===== PROXIMITY LOOKUP =====
function ProximityLookup({ properties, clients, jobs }) {
  const [searchAddr, setSearchAddr] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const geocoderRef = useRef(null);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const selectedPlaceRef = useRef(null);

  const { isLoaded } = useGoogleMaps();

  // Initialize Google Places Autocomplete on the input
  useEffect(() => {
    if (!isLoaded || !autocompleteInputRef.current || autocompleteRef.current) return;
    if (!window.google?.maps?.places?.Autocomplete) return;

    const ac = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['geometry', 'formatted_address'],
    });

    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (place?.geometry?.location) {
        selectedPlaceRef.current = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setSearchAddr(place.formatted_address || autocompleteInputRef.current.value);
      }
    });

    autocompleteRef.current = ac;
  }, [isLoaded]);

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.id] = c; });
    return m;
  }, [clients]);

  // Build a map of clientId -> upcoming jobs (sorted by date)
  const upcomingJobsByClient = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const m = {};
    jobs
      .filter(j => j.date >= todayStr && j.status !== 'cancelled')
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .forEach(j => {
        if (!m[j.clientId]) m[j.clientId] = [];
        m[j.clientId].push(j);
      });
    return m;
  }, [jobs]);

  const propertiesWithCoords = useMemo(() => {
    return properties
      .filter(p => p.coordinates?.lat && p.coordinates?.lng)
      .map(p => {
        const client = clientMap[p.clientId];
        const upcoming = upcomingJobsByClient[p.clientId] || [];
        return {
          ...p,
          clientId: p.clientId,
          clientName: client ? formatFullName(client) : 'Unknown',
          clientType: client?.clientType || 'residential',
          frequency: client?.frequency || 'on_demand',
          preferredDay: client?.preferredDay || '',
          clientStatus: client?.status || 'inactive',
          upcomingJobs: upcoming.slice(0, 3),
        };
      })
      .filter(p => p.clientStatus === 'active');
  }, [properties, clientMap, upcomingJobsByClient]);

  const calcDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const computeResults = useCallback((lat, lng) => {
    const withDist = propertiesWithCoords.map(p => ({
      ...p,
      distance: calcDistance(lat, lng, p.coordinates.lat, p.coordinates.lng),
    })).sort((a, b) => a.distance - b.distance);
    setResults(withDist.slice(0, 8));
  }, [propertiesWithCoords]);

  const doLookup = useCallback(() => {
    if (!searchAddr.trim()) return;
    setLoading(true);
    setError('');
    setResults(null);

    // If user selected a place from autocomplete, use its coordinates directly
    if (selectedPlaceRef.current) {
      const { lat, lng } = selectedPlaceRef.current;
      selectedPlaceRef.current = null;
      computeResults(lat, lng);
      setLoading(false);
      return;
    }

    // Fall back to geocoding for manually typed addresses
    if (isLoaded && window.google?.maps?.Geocoder) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocoderRef.current.geocode({ address: searchAddr + ', Virginia' }, (res, status) => {
        setLoading(false);
        if (status === 'OK' && res[0]) {
          const loc = res[0].geometry.location;
          computeResults(loc.lat(), loc.lng());
        } else {
          setError('Address not found. Try including city or zip.');
        }
      });
    } else {
      setLoading(false);
      setError('Maps not loaded yet. Please try again in a moment.');
    }
  }, [searchAddr, computeResults, isLoaded]);

  const freqLabels = { weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', on_demand: 'On Demand' };
  const typeLabels = { residential: 'Res', property_manager: 'PM', commercial: 'Comm' };
  const typeColors = { residential: 'var(--sage)', property_manager: 'var(--gold)', commercial: 'var(--teal)' };

  const formatJobDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEE, MMM d');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="dash-proximity">
      <div className="dash-proximity-search">
        <div className="dash-proximity-input-wrap">
          <MapPin size={16} />
          <input
            ref={autocompleteInputRef}
            value={searchAddr}
            onChange={e => { setSearchAddr(e.target.value); selectedPlaceRef.current = null; }}
            onKeyDown={e => e.key === 'Enter' && doLookup()}
            placeholder="Enter address to find nearby clients..."
          />
          {searchAddr && (
            <button className="dash-proximity-clear" onClick={() => { setSearchAddr(''); setResults(null); setError(''); selectedPlaceRef.current = null; if (autocompleteInputRef.current) autocompleteInputRef.current.value = ''; }}>
              <X size={14} />
            </button>
          )}
        </div>
        <button className="dash-proximity-btn" onClick={doLookup} disabled={loading || !searchAddr.trim()}>
          {loading ? 'Searching...' : 'Find Nearby'}
        </button>
      </div>
      {error && <div className="dash-proximity-error">{error}</div>}
      {results && (
        <div className="dash-proximity-results">
          {results.length === 0 && (
            <div className="dash-proximity-empty">
              <p>No active clients with mapped addresses found nearby.</p>
            </div>
          )}
          {results.map((p, i) => (
            <Link key={p.id} to={`/admin/clients/${p.clientId}`} className={`dash-proximity-row ${i === 0 ? 'closest' : ''}`}>
              <div className={`dash-proximity-dist ${i === 0 ? 'closest' : ''}`}>
                {p.distance.toFixed(1)}
              </div>
              <div className="dash-proximity-info">
                <div className="dash-proximity-name-row">
                  <span className="dash-proximity-name">{p.clientName}</span>
                  <span className="dash-proximity-type" style={{ color: typeColors[p.clientType], background: `${typeColors[p.clientType]}15` }}>
                    {typeLabels[p.clientType]}
                  </span>
                </div>
                <div className="dash-proximity-addr">{p.addressLine1}, {p.city}</div>
                <div className="dash-proximity-schedule">
                  {p.preferredDay && (
                    <span className="dash-proximity-day">
                      <Calendar size={11} />
                      {p.preferredDay}s · {freqLabels[p.frequency] || p.frequency}
                    </span>
                  )}
                  {!p.preferredDay && p.frequency !== 'on_demand' && (
                    <span className="dash-proximity-day">
                      <Calendar size={11} />
                      {freqLabels[p.frequency] || p.frequency}
                    </span>
                  )}
                  {p.upcomingJobs.length > 0 ? (
                    <span className="dash-proximity-next">
                      Next: {formatJobDate(p.upcomingJobs[0].date)}
                      {p.upcomingJobs[0].scheduledTime && ` at ${p.upcomingJobs[0].scheduledTime}`}
                    </span>
                  ) : (
                    <span className="dash-proximity-next none">No upcoming apts</span>
                  )}
                </div>
              </div>
              <div className="dash-proximity-miles">{p.distance.toFixed(1)} mi</div>
            </Link>
          ))}
          <div className="dash-proximity-note">Distances are approximate · Sorted nearest first · Click to view client</div>
        </div>
      )}
      {!results && !error && (
        <div className="dash-proximity-empty">
          <MapPin size={32} className="dash-proximity-empty-icon" />
          <p>Enter an address to see which active clients are nearby and their upcoming schedules</p>
        </div>
      )}
    </div>
  );
}

// ===== MAIN DASHBOARD =====
function Dashboard() {
  const { state, toggleTodo, addTodo, updateTodo, deleteTodo } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { clients, employees, properties, estimates, jobs, invoices, todos, messages, complaints, feedbacks } = state;

  const [todoFilter, setTodoFilter] = useState('active');
  const [showAddTodo, setShowAddTodo] = useState(false);
  const [newTodo, setNewTodo] = useState({ text: '', priority: 'medium', category: 'sales' });
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editTodoText, setEditTodoText] = useState('');
  const [editTodoPriority, setEditTodoPriority] = useState('medium');
  const [editTodoCategory, setEditTodoCategory] = useState('sales');
  const [recapStatus, setRecapStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'

  // User name
  const firstName = useMemo(() => {
    if (!user?.email) return '';
    if (ADMIN_NAMES[user.email.toLowerCase()]) return ADMIN_NAMES[user.email.toLowerCase()];
    const emp = employees.find(e => e.email?.toLowerCase() === user.email.toLowerCase());
    if (emp) return emp.firstName || '';
    return user.email.split('@')[0];
  }, [user, employees]);

  // Dates
  const today = format(new Date(), 'yyyy-MM-dd');
  const thisMonthStart = startOfMonth(new Date());
  const thisMonthEnd = endOfMonth(new Date());
  const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
  const lastMonthEnd = endOfMonth(subMonths(new Date(), 1));

  // Today stats
  const todayJobs = useMemo(() => jobs.filter(j => j.date === today), [jobs, today]);
  const todayTeams = useMemo(() => {
    const s = new Set();
    todayJobs.forEach(j => { if (j.team?.length) s.add([...j.team].sort().join(',')); });
    return s.size;
  }, [todayJobs]);

  // Revenue
  const revenueStats = useMemo(() => {
    const inRange = (inv, start, end) => {
      if (!inv.createdAt) return false;
      try { return isWithinInterval(parseISO(inv.createdAt), { start, end }); } catch { return false; }
    };
    const thisMonth = invoices.filter(i => inRange(i, thisMonthStart, thisMonthEnd));
    const lastMonth = invoices.filter(i => inRange(i, lastMonthStart, lastMonthEnd));
    const paid = thisMonth.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
    const pending = thisMonth.filter(i => i.status === 'pending').reduce((s, i) => s + (i.total || 0), 0);
    const overdue = thisMonth.filter(i => i.status === 'overdue');
    const overdueTotal = overdue.reduce((s, i) => s + (i.total || 0), 0);
    const lastPaid = lastMonth.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
    const mom = lastPaid > 0 ? ((paid - lastPaid) / lastPaid) * 100 : 0;
    return { paid, pending, overdueCount: overdue.length, overdueTotal, lastPaid, mom };
  }, [invoices, thisMonthStart, thisMonthEnd, lastMonthStart, lastMonthEnd]);

  // Estimates
  const estimateStats = useMemo(() => {
    const open = estimates.filter(e => e.status === 'sent' || e.status === 'pending_review');
    const pr = estimates.filter(e => e.status === 'pending_review').length;
    const last30 = estimates.filter(e => {
      try { return parseISO(e.createdAt) >= subMonths(new Date(), 1); } catch { return false; }
    });
    const won = last30.filter(e => e.status === 'approved' || e.status === 'converted').length;
    const decided = last30.filter(e => ['approved', 'converted', 'declined'].includes(e.status)).length;
    return { openCount: open.length, pendingReview: pr, convRate: decided > 0 ? Math.round((won / decided) * 100) : 0 };
  }, [estimates]);

  // Labor cost %
  const laborCostPct = useMemo(() => {
    const completed = jobs.filter(j => (j.status === 'completed' || j.actual_duration_min > 0) && j.price > 0);
    if (completed.length === 0) return { pct: 0, hasData: false };
    let rev = 0, cost = 0;
    completed.forEach(job => {
      const team = (job.team || []).map(id => employees.find(e => e.id === id)).filter(Boolean);
      const rate = team.length > 0 ? team.reduce((s, e) => s + (e.hourlyRate || 18), 0) / team.length : 18;
      cost += ((job.actual_duration_min || 0) / 60) * rate * team.length;
      rev += job.price;
    });
    return { pct: rev > 0 ? Math.round((cost / rev) * 100) : 0, hasData: true };
  }, [jobs, employees]);

  // Weekly revenue chart
  const weeklyRevenue = useMemo(() => {
    const weeks = [];
    const now = new Date();
    for (let i = 4; i >= 0; i--) {
      const ws = startOfWeek(addDays(now, -i * 7), { weekStartsOn: 1 });
      const we = addDays(ws, 6);
      let weekInvs = invoices.filter(inv => {
        if (inv.status !== 'paid') return false;
        const dateField = inv.paidAt || inv.createdAt;
        if (!dateField) return false;
        try { return isWithinInterval(parseISO(dateField), { start: ws, end: we }); } catch { return false; }
      });
      weeks.push({ label: format(ws, 'MMM d'), amount: weekInvs.reduce((s, inv) => s + (inv.total || 0), 0) });
    }
    return weeks;
  }, [invoices]);

  // Pipeline
  const pipeline = useMemo(() => {
    const active = estimates
      .filter(e => ['pending_review', 'sent', 'approved', 'declined'].includes(e.status))
      .sort((a, b) => {
        const o = { pending_review: 0, sent: 1, approved: 2, declined: 3 };
        return (o[a.status] ?? 99) - (o[b.status] ?? 99);
      }).slice(0, 6);
    const total = estimates.filter(e => ['pending_review', 'sent'].includes(e.status))
      .reduce((s, e) => s + (e.recurringTotal || e.deepCleanTotal || 0), 0);
    const sc = {
      pending_review: estimates.filter(e => e.status === 'pending_review').length,
      sent: estimates.filter(e => e.status === 'sent').length,
      approved: estimates.filter(e => e.status === 'approved').length,
      declined: estimates.filter(e => e.status === 'declined').length,
    };
    return { active, total, sc, totalForBar: Object.values(sc).reduce((s, v) => s + v, 0) || 1 };
  }, [estimates]);

  // Recent invoices
  const recentInvoices = useMemo(() =>
    [...invoices].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 6),
    [invoices]);

  // Client mix
  const clientMix = useMemo(() => {
    const ac = clients.filter(c => c.status === 'active');
    const types = ['residential', 'commercial', 'property_manager'];
    const mix = types.map(type => {
      const tc = ac.filter(c => c.clientType === type);
      let annual = 0;
      tc.forEach(client => {
        const cj = jobs.filter(j => j.clientId === client.id && j.price > 0).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        if (cj.length > 0) annual += cj[0].price * annualMultiplier(client.frequency);
      });
      return {
        type, label: type === 'property_manager' ? 'Property Mgr' : type === 'commercial' ? 'Commercial' : 'Residential',
        count: tc.length, annual, pct: ac.length > 0 ? Math.round((tc.length / ac.length) * 100) : 0,
      };
    });
    return { mix, totalAnnual: mix.reduce((s, m) => s + m.annual, 0) };
  }, [clients, jobs]);

  // Unread messages
  const unreadMessages = useMemo(() =>
    messages.filter(m => m.status === 'new' || !m.lastAdminReadAt).length,
    [messages]);

  const leadsNeedingAttention = estimateStats.pendingReview + (revenueStats.overdueCount > 0 ? 1 : 0);

  // Todos
  const filteredTodos = useMemo(() => {
    return todos.filter(t => {
      if (todoFilter === 'all') return true;
      if (todoFilter === 'active') return !t.done;
      if (todoFilter === 'done') return t.done;
      return t.category === todoFilter;
    }).sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      const p = { high: 0, medium: 1, low: 2 };
      return (p[a.priority] ?? 1) - (p[b.priority] ?? 1);
    });
  }, [todos, todoFilter]);

  const handleAddTodo = async () => {
    if (!newTodo.text.trim()) return;
    await addTodo(newTodo);
    setNewTodo({ text: '', priority: 'medium', category: 'sales' });
    setShowAddTodo(false);
  };

  // Helpers
  const getEstimateClientName = (est) => {
    const c = clients.find(c => c.id === est.clientId);
    return c ? formatFullName(c) : 'Unknown Client';
  };
  const getEstimateAddress = (est) => {
    if (est.tempPropertyData) return est.tempPropertyData.addressLine1;
    const p = properties.find(p => p.id === est.propertyId);
    return p ? `${p.addressLine1}, ${p.city}` : '';
  };
  const getEstimateAmount = (est) => est.recurringTotal || est.deepCleanTotal || 0;
  const isWebLead = (est) => est.source?.startsWith('website_') || est.createdBy === 'quote_form';
  const getServiceLabel = (est) => {
    const f = est.calculatorInputs?.frequency;
    return { weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', one_time: 'One-Time', deep_clean: 'Deep Clean' }[f] || 'Cleaning';
  };

  const statusStyle = (status) => ({
    pending_review: { bg: '#fef3c7', color: '#92400e', label: 'Needs Review' },
    sent: { bg: 'rgba(184,146,106,0.15)', color: 'var(--gold)', label: 'Sent' },
    approved: { bg: 'rgba(34,197,94,0.1)', color: 'var(--green)', label: 'Approved' },
    declined: { bg: 'rgba(239,68,68,0.1)', color: 'var(--red)', label: 'Declined' },
    converted: { bg: 'rgba(44,95,78,0.1)', color: 'var(--sage)', label: 'Converted' },
    paid: { bg: 'rgba(34,197,94,0.1)', color: 'var(--green)', label: 'Paid' },
    pending: { bg: 'rgba(184,146,106,0.15)', color: 'var(--gold)', label: 'Pending' },
    overdue: { bg: 'rgba(239,68,68,0.1)', color: 'var(--red)', label: 'Overdue' },
    draft: { bg: 'var(--cream-dark)', color: 'var(--text-muted)', label: 'Draft' },
  }[status] || { bg: '#f3f4f6', color: '#6b7280', label: status });

  const priorityStyles = {
    high: { color: 'var(--red)', bg: 'rgba(239,68,68,0.08)' },
    medium: { color: 'var(--gold)', bg: 'rgba(184,146,106,0.1)' },
    low: { color: 'var(--text-muted)', bg: 'var(--cream)' },
  };

  const activeClientCount = clients.filter(c => c.status === 'active').length;

  const kpis = [
    { label: `Revenue (${format(new Date(), 'MMM')})`, value: formatCurrency(revenueStats.paid).replace('.00', ''), sub: `${formatCurrency(revenueStats.pending).replace('.00', '')} pending`, accent: 'var(--sage)', icon: <DollarSign size={18} /> },
    { label: 'Active Clients', value: activeClientCount, sub: `${clients.filter(c => c.status === 'inactive').length} inactive`, accent: 'var(--teal)', icon: <Users size={18} /> },
    { label: 'Open Estimates', value: estimateStats.openCount, sub: estimateStats.pendingReview > 0 ? `${estimateStats.pendingReview} need review` : 'All reviewed', accent: 'var(--gold)', icon: <FileText size={18} /> },
    { label: 'Conversion Rate', value: `${estimateStats.convRate}%`, sub: 'Last 30 days', accent: 'var(--sage)', icon: <TrendingUp size={18} /> },
    { label: 'Labor Cost %', value: laborCostPct.hasData ? `${laborCostPct.pct}%` : '—', sub: laborCostPct.hasData ? (laborCostPct.pct <= 40 ? 'On target ✓' : laborCostPct.pct <= 50 ? 'Above target' : 'Needs attention') : 'No data yet', accent: laborCostPct.pct > 50 ? 'var(--red)' : laborCostPct.pct > 40 ? 'var(--gold)' : 'var(--sage)', icon: <Percent size={18} /> },
    { label: 'Overdue Invoices', value: revenueStats.overdueCount, sub: revenueStats.overdueCount > 0 ? formatCurrency(revenueStats.overdueTotal).replace('.00', '') : 'All clear ✓', accent: revenueStats.overdueCount > 0 ? 'var(--red)' : 'var(--sage)', icon: <AlertTriangle size={18} /> },
  ];

  // ===== STAFF PROFIT LEADERBOARD =====
  const [lbTimeRecords, setLbTimeRecords] = useState([]);
  const [lbView, setLbView] = useState('week'); // 'week' | 'month'
  const [lbMonthOffset, setLbMonthOffset] = useState(0); // 0 = current month, -1 = last month, etc.
  
  // Date ranges
  const weekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const lbMonthStart = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + lbMonthOffset);
    return startOfMonth(d);
  }, [lbMonthOffset]);
  const lbMonthEnd = useMemo(() => endOfMonth(lbMonthStart), [lbMonthStart]);
  
  // Load time records covering both week and selected month - REAL-TIME
  useEffect(() => {
    // Query from earliest of weekStart or monthStart to today
    const weekStr = format(weekStart, 'yyyy-MM-dd');
    const monthStr = format(lbMonthStart, 'yyyy-MM-dd');
    const rangeStart = weekStr < monthStr ? weekStr : monthStr;
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    
    const recordsRef = collection(db, 'timeRecords');
    const q = query(recordsRef, where('date', '>=', rangeStart), where('date', '<=', todayStr));
    
    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLbTimeRecords(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('Error in time records listener:', err);
    });
    
    return () => unsubscribe();
  }, [weekStart, lbMonthStart, lbMonthEnd]);

  // Shared profit calculation helper
  const calcLeaderboard = useCallback((dateList, timeRecs) => {
    if (dateList.length === 0) return [];

    const empRates = {};
    employees.forEach(emp => {
      empRates[emp.id] = { hourlyRate: emp.hourlyRate || 15, name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(), firstName: emp.firstName };
    });

    const profitByPerson = {};
    
    dateList.forEach(dateStr => {
      const dayJobs = jobs.filter(j => j.date === dateStr && j.status === 'completed');
      
      // === Per-job profit split (accounts for helpers on individual jobs) ===
      // First gather all team+helper combos per job
      const jobEntries = dayJobs.map(job => {
        const jobTeam = [...new Set([...(job.team || []), ...(job.helpers || [])])];
        return { job, jobTeam };
      });

      // Gather all unique employees working today for labor cost lookup
      const allTodayEmpIds = [...new Set(jobEntries.flatMap(e => e.jobTeam))];

      // Get time records for labor cost
      const dayRecords = timeRecs.filter(r => r.date === dateStr && allTodayEmpIds.includes(r.employeeId));
      const clockedHours = {};
      dayRecords.forEach(r => { if (r.hoursWorked) clockedHours[r.employeeId] = r.hoursWorked; });

      // Calculate per-job revenue & labor split
      jobEntries.forEach(({ job, jobTeam }) => {
        if (jobTeam.length === 0) return;

        const jobRevenue = job.price || 0;
        const jobHours = job.duration || 0;
        const driveHours = 0.25; // estimate 15 min drive per job

        // Labor cost for this job
        let jobLaborCost = 0;
        if (Object.keys(clockedHours).length > 0) {
          // Proportional: each person's clocked time × rate, scaled to job's share of their day
          jobTeam.forEach(empId => {
            const rate = empRates[empId]?.hourlyRate || 15;
            // Use job duration as proxy for how much of their day this job represents
            jobLaborCost += (jobHours + driveHours) * rate;
          });
        } else {
          // Fallback: estimated hours × avg rate × team size
          const avgRate = jobTeam.reduce((s, id) => s + (empRates[id]?.hourlyRate || 15), 0) / jobTeam.length;
          jobLaborCost = (jobHours + driveHours) * avgRate * jobTeam.length;
        }

        const jobProfit = jobRevenue - jobLaborCost;
        const profitPerPerson = jobProfit / jobTeam.length;
        const revenuePerPerson = jobRevenue / jobTeam.length;

        jobTeam.forEach(empId => {
          if (!profitByPerson[empId]) {
            profitByPerson[empId] = { id: empId, name: empRates[empId]?.name || 'Unknown', firstName: empRates[empId]?.firstName || '?', totalProfit: 0, totalRevenue: 0, routeCount: 0, days: new Set() };
          }
          profitByPerson[empId].totalProfit += profitPerPerson;
          profitByPerson[empId].totalRevenue += revenuePerPerson;
          profitByPerson[empId].days.add(dateStr);
        });
      });

      // Count unique routes per person for display
      const routesByPerson = {};
      dayJobs.forEach(job => {
        const jobTeam = [...new Set([...(job.team || []), ...(job.helpers || [])])];
        jobTeam.forEach(empId => {
          if (!routesByPerson[empId]) routesByPerson[empId] = new Set();
          routesByPerson[empId].add(job.routeId || 'unassigned');
        });
      });
      Object.entries(routesByPerson).forEach(([empId, routeSet]) => {
        if (profitByPerson[empId]) profitByPerson[empId].routeCount += routeSet.size;
      });
    });

    return Object.values(profitByPerson)
      .map(p => ({ ...p, daysWorked: p.days.size, margin: p.totalRevenue > 0 ? (p.totalProfit / p.totalRevenue * 100) : 0 }))
      .sort((a, b) => b.totalProfit - a.totalProfit);
  }, [jobs, employees]);

  // Weekly leaderboard - includes today's completed jobs
  const weeklyLeaderboard = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const completedDays = [];
    for (let i = 0; i < 7; i++) {
      const dStr = format(addDays(weekStart, i), 'yyyy-MM-dd');
      if (dStr <= todayStr) completedDays.push(dStr); // Include today
    }
    // Filter to only include days that have at least one completed job
    const daysWithCompletedJobs = completedDays.filter(dateStr => 
      jobs.some(j => j.date === dateStr && j.status === 'completed')
    );
    const entries = calcLeaderboard(daysWithCompletedJobs, lbTimeRecords);
    const dayLabels = daysWithCompletedJobs.map(d => format(parseISO(d), 'EEE'));
    return { entries, dayCount: daysWithCompletedJobs.length, dayLabels };
  }, [weekStart, lbTimeRecords, calcLeaderboard, jobs]);

  // Monthly leaderboard - includes today's completed jobs
  const monthlyLeaderboard = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const completedDays = [];
    const daysInMonth = lbMonthEnd.getDate();
    for (let i = 0; i < daysInMonth; i++) {
      const d = addDays(lbMonthStart, i);
      const dStr = format(d, 'yyyy-MM-dd');
      if (dStr <= todayStr) completedDays.push(dStr); // Include today
    }
    // Filter to only include days that have at least one completed job
    const daysWithCompletedJobs = completedDays.filter(dateStr => 
      jobs.some(j => j.date === dateStr && j.status === 'completed')
    );
    
    const entries = calcLeaderboard(daysWithCompletedJobs, lbTimeRecords);
    const monthLabel = format(lbMonthStart, 'MMMM yyyy');
    return { entries, dayCount: daysWithCompletedJobs.length, monthLabel };
  }, [lbMonthStart, lbMonthEnd, lbTimeRecords, calcLeaderboard, jobs]);

  const activeLb = lbView === 'week' ? weeklyLeaderboard : monthlyLeaderboard;

  // ===== COMPLAINTS & FEEDBACK =====
  const recentComplaints = useMemo(() =>
    [...(complaints || [])].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 5),
    [complaints]);

  const openComplaintCount = useMemo(() =>
    (complaints || []).filter(c => c.status === 'open').length,
    [complaints]);

  const recentFeedbacks = useMemo(() =>
    [...(feedbacks || [])].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 5),
    [feedbacks]);

  const getComplaintClientName = (clientId) => {
    const c = clients.find(c => c.id === clientId);
    return c ? formatFullName(c) : 'Unknown';
  };

  const getComplaintEmployeeNames = (empIds) => {
    if (!empIds?.length) return '';
    return empIds.map(id => {
      const e = employees.find(e => e.id === id);
      return e ? e.firstName : '?';
    }).join(', ');
  };

  const sourceIcons = { phone: Phone, email: Mail, in_person: User, google_review: Globe, text: MessageCircle };

  const handleSendRecap = async () => {
    if (recapStatus === 'sending') return;
    setRecapStatus('sending');
    try {
      await sendWeeklyRecap();
      setRecapStatus('sent');
      setTimeout(() => setRecapStatus('idle'), 4000);
    } catch (err) {
      console.error('Failed to send recap:', err);
      setRecapStatus('error');
      setTimeout(() => setRecapStatus('idle'), 4000);
    }
  };

  const recapDesc = recapStatus === 'sending' ? 'Sending...'
    : recapStatus === 'sent' ? 'Sent!'
    : recapStatus === 'error' ? 'Failed'
    : 'Email summary';

  const quickActions = [
    { icon: <FileText size={20} />, label: 'New Estimate', desc: 'Create or send', path: '/admin/estimates/new' },
    { icon: <Map size={20} />, label: 'Dispatch', desc: 'Manage routes', path: '/admin/dispatch' },
    { icon: <Receipt size={20} />, label: 'New Invoice', desc: 'Bill a client', path: '/admin/invoices/new' },
    { icon: <UserPlus size={20} />, label: 'Add Client', desc: 'New customer', path: '/admin/clients' },
    { icon: <BarChart3 size={20} />, label: 'Reports', desc: 'Revenue & labor', path: '/admin/reports' },
    { icon: <MessageSquare size={20} />, label: 'Messages', desc: unreadMessages > 0 ? `${unreadMessages} unread` : 'Client comms', path: '/admin/messages' },
    { icon: <Calendar size={20} />, label: 'Calendar', desc: 'Week view', path: '/admin/calendar' },
    { icon: <Settings size={20} />, label: 'Settings', desc: 'App config', path: '/admin/settings' },
    { icon: recapStatus === 'sending' ? <Loader size={20} className="spin" /> : <Send size={20} />, label: 'Weekly Recap', desc: recapDesc, action: handleSendRecap },
  ];

  return (
    <div className="dash">
      {/* Header */}
      <div className="dash-header">
        <div className="dash-header-text">
          <h1>{getGreeting()}, {firstName || 'Boss'}</h1>
          <span className="dash-header-date">{format(new Date(), 'EEEE, MMM d')}</span>
        </div>
        <p className="dash-header-summary">
          {todayTeams > 0 ? `${todayTeams} team${todayTeams > 1 ? 's' : ''} dispatched` : 'No teams dispatched'} today · {todayJobs.length} job{todayJobs.length !== 1 ? 's' : ''} scheduled
          {leadsNeedingAttention > 0 && ` · ${leadsNeedingAttention} item${leadsNeedingAttention > 1 ? 's' : ''} need attention`}
        </p>
      </div>

      {/* KPIs */}
      <div className="dash-kpi-row">
        {kpis.map((kpi, i) => (
          <div key={i} className="dash-kpi" style={{ borderTopColor: kpi.accent }}>
            <div className="dash-kpi-icon" style={{ color: kpi.accent }}>{kpi.icon}</div>
            <div className="dash-kpi-label">{kpi.label}</div>
            <div className="dash-kpi-value">{kpi.value}</div>
            <div className="dash-kpi-sub">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Sales + Todos */}
      <div className="dash-two-col">
        {/* Lead Pipeline */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h2>Lead Pipeline</h2>
            <Link to="/admin/estimates" className="dash-view-all">View All <ArrowRight size={14} /></Link>
          </div>
          <div className="dash-pipeline-list">
            {pipeline.active.length === 0 && <div className="dash-empty">No active leads right now</div>}
            {pipeline.active.map(est => {
              const st = statusStyle(est.status);
              return (
                <Link key={est.id} to="/admin/estimates" className={`dash-pipeline-row ${est.status === 'pending_review' ? 'needs-review' : ''}`}>
                  <div className="dash-pipeline-info">
                    <div className="dash-pipeline-name-row">
                      <span className="dash-pipeline-name">{getEstimateClientName(est)}</span>
                      {isWebLead(est) && <span className="dash-web-lead-tag">Web</span>}
                      {est.status === 'pending_review' && <span className="dash-new-lead-tag">NEW</span>}
                    </div>
                    <span className="dash-pipeline-detail">{getServiceLabel(est)} · {getEstimateAddress(est)}</span>
                  </div>
                  <div className="dash-pipeline-amount">{formatCurrency(getEstimateAmount(est)).replace('.00', '')}</div>
                  <span className="dash-status-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                </Link>
              );
            })}
          </div>
          <div className="dash-pipeline-summary">
            <div className="dash-pipeline-summary-top">
              <span>Pipeline Total</span>
              <strong>{formatCurrency(pipeline.total).replace('.00', '')}</strong>
            </div>
            <div className="dash-pipeline-bar">
              {pipeline.sc.pending_review > 0 && <div className="dash-bar-seg review" style={{ width: `${(pipeline.sc.pending_review / pipeline.totalForBar) * 100}%` }} />}
              {pipeline.sc.sent > 0 && <div className="dash-bar-seg sent" style={{ width: `${(pipeline.sc.sent / pipeline.totalForBar) * 100}%` }} />}
              {pipeline.sc.approved > 0 && <div className="dash-bar-seg approved" style={{ width: `${(pipeline.sc.approved / pipeline.totalForBar) * 100}%` }} />}
              {pipeline.sc.declined > 0 && <div className="dash-bar-seg declined" style={{ width: `${(pipeline.sc.declined / pipeline.totalForBar) * 100}%` }} />}
            </div>
            <div className="dash-pipeline-legend">
              <span><i className="dot review" />New</span>
              <span><i className="dot sent" />Sent</span>
              <span><i className="dot approved" />Won</span>
              <span><i className="dot declined" />Lost</span>
            </div>
          </div>
        </div>

        {/* To-Do */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h2>To-Do</h2>
            <div className="dash-todo-header-right">
              <span className="dash-todo-remaining">{todos.filter(t => !t.done).length} remaining</span>
              <Link to="/admin/todos" className="dash-view-all">Full List <ArrowRight size={14} /></Link>
            </div>
          </div>
          <div className="dash-todo-filters">
            {['active', 'all', 'sales', 'billing', 'team', 'ops', 'done'].map(f => (
              <button key={f} className={`dash-todo-pill ${todoFilter === f ? 'active' : ''}`} onClick={() => setTodoFilter(f)}>{f}</button>
            ))}
          </div>
          <div className="dash-todo-list">
            {filteredTodos.length === 0 && <div className="dash-empty">{todoFilter === 'active' ? 'All caught up! 🎉' : 'No items match this filter'}</div>}
            {filteredTodos.slice(0, 8).map(t => (
              editingTodoId === t.id ? (
                <div key={t.id} className="dash-todo-edit-row">
                  <input
                    autoFocus
                    className="dash-todo-edit-input"
                    value={editTodoText}
                    onChange={e => setEditTodoText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && editTodoText.trim()) {
                        updateTodo(t.id, { text: editTodoText.trim(), priority: editTodoPriority, category: editTodoCategory });
                        setEditingTodoId(null);
                      }
                      if (e.key === 'Escape') setEditingTodoId(null);
                    }}
                  />
                  <select value={editTodoPriority} onChange={e => setEditTodoPriority(e.target.value)} className="dash-todo-edit-select">
                    <option value="high">High</option><option value="medium">Med</option><option value="low">Low</option>
                  </select>
                  <select value={editTodoCategory} onChange={e => setEditTodoCategory(e.target.value)} className="dash-todo-edit-select">
                    <option value="sales">Sales</option><option value="billing">Billing</option><option value="team">Team</option><option value="ops">Ops</option><option value="marketing">Mktg</option>
                  </select>
                  <button className="dash-todo-edit-save" onClick={() => { updateTodo(t.id, { text: editTodoText.trim(), priority: editTodoPriority, category: editTodoCategory }); setEditingTodoId(null); }}>Save</button>
                  <button className="dash-todo-edit-cancel" onClick={() => setEditingTodoId(null)}><X size={14} /></button>
                </div>
              ) : (
                <div key={t.id} className={`dash-todo-item ${t.done ? 'done' : ''}`}>
                  <div className={`dash-todo-check ${t.done ? 'checked' : ''}`} style={!t.done ? { borderColor: priorityStyles[t.priority]?.color } : {}} onClick={() => toggleTodo(t.id)}>
                    {t.done && <CheckCircle size={14} />}
                  </div>
                  <span className="dash-todo-text" onClick={() => toggleTodo(t.id)}>{t.text}</span>
                  <span className="dash-todo-priority" style={{ color: priorityStyles[t.priority]?.color, background: priorityStyles[t.priority]?.bg }}>{t.priority}</span>
                  <button className="dash-todo-action-btn" onClick={(e) => { e.stopPropagation(); setEditingTodoId(t.id); setEditTodoText(t.text); setEditTodoPriority(t.priority || 'medium'); setEditTodoCategory(t.category || 'sales'); }} title="Edit"><Edit3 size={13} /></button>
                  <button className="dash-todo-action-btn delete" onClick={(e) => { e.stopPropagation(); deleteTodo(t.id); }} title="Delete"><Trash2 size={13} /></button>
                </div>
              )
            ))}
          </div>
          {showAddTodo ? (
            <div className="dash-todo-add-form">
              <input autoFocus value={newTodo.text} onChange={e => setNewTodo(p => ({ ...p, text: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAddTodo()} placeholder="What needs to be done?" />
              <div className="dash-todo-add-options">
                <select value={newTodo.priority} onChange={e => setNewTodo(p => ({ ...p, priority: e.target.value }))}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
                <select value={newTodo.category} onChange={e => setNewTodo(p => ({ ...p, category: e.target.value }))}>
                  <option value="sales">Sales</option><option value="billing">Billing</option><option value="team">Team</option><option value="ops">Ops</option><option value="marketing">Marketing</option>
                </select>
                <button className="dash-todo-add-save" onClick={handleAddTodo}>Add</button>
                <button className="dash-todo-add-cancel" onClick={() => setShowAddTodo(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="dash-todo-add-btn" onClick={() => setShowAddTodo(true)}><Plus size={14} /> Add task</button>
          )}
        </div>
      </div>

      {/* Staff Profit Leaderboard */}
      {(activeLb.entries.length > 0 || lbView === 'month') && (
        <div className="dash-card dash-leaderboard">
          <div className="dash-card-header">
            <h2><Trophy size={18} style={{ color: '#b8926a' }} /> Staff Leaderboard <span className="live-badge">● Live</span></h2>
            <div className="dash-lb-controls">
              <div className="dash-lb-toggle">
                <button className={lbView === 'week' ? 'active' : ''} onClick={() => setLbView('week')}>Week</button>
                <button className={lbView === 'month' ? 'active' : ''} onClick={() => { setLbView('month'); setLbMonthOffset(0); }}>Month</button>
              </div>
            </div>
          </div>

          {/* Period label + month nav */}
          <div className="dash-lb-period-bar">
            {lbView === 'week' ? (
              <span className="dash-lb-period-label">
                This week · {weeklyLeaderboard.dayLabels.join(', ')}{weeklyLeaderboard.dayCount === 0 ? ' (no completed days yet)' : ''}
              </span>
            ) : (
              <div className="dash-lb-month-nav">
                <button className="dash-lb-month-arrow" onClick={() => setLbMonthOffset(o => o - 1)}>‹</button>
                <span className="dash-lb-period-label">{monthlyLeaderboard.monthLabel} · {monthlyLeaderboard.dayCount} day{monthlyLeaderboard.dayCount !== 1 ? 's' : ''}</span>
                <button className="dash-lb-month-arrow" onClick={() => setLbMonthOffset(o => Math.min(0, o + 1))} disabled={lbMonthOffset >= 0}>›</button>
              </div>
            )}
          </div>

          <div className="dash-leaderboard-list">
            {activeLb.entries.length === 0 && (
              <div className="dash-empty" style={{ padding: '24px 20px' }}>No completed routes for this period yet</div>
            )}
            {activeLb.entries.map((person, idx) => {
              const maxProfit = activeLb.entries[0]?.totalProfit || 1;
              const barPct = maxProfit > 0 ? Math.max(5, (person.totalProfit / maxProfit) * 100) : 5;
              const rankIcon = idx === 0 ? <Trophy size={16} /> : idx === 1 ? <Award size={16} /> : idx === 2 ? <Medal size={16} /> : null;
              const rankColors = ['#b8926a', '#94a3b8', '#b87333'];
              return (
                <div key={person.id} className={`dash-lb-row ${idx < 3 ? 'top-three' : ''}`}>
                  <div className="dash-lb-rank" style={{ color: idx < 3 ? rankColors[idx] : 'var(--text-muted)' }}>
                    {rankIcon || <span className="dash-lb-rank-num">{idx + 1}</span>}
                  </div>
                  <div className="dash-lb-info">
                    <div className="dash-lb-name-row">
                      <span className="dash-lb-name">{person.name}</span>
                      <span className="dash-lb-stats">{person.daysWorked}d · {person.routeCount} route{person.routeCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="dash-lb-bar-track">
                      <div 
                        className={`dash-lb-bar-fill ${person.totalProfit < 0 ? 'negative' : ''}`} 
                        style={{ width: `${person.totalProfit > 0 ? barPct : 5}%` }} 
                      />
                    </div>
                  </div>
                  <div className="dash-lb-profit">
                    <span className={`dash-lb-amount ${person.totalProfit < 0 ? 'negative' : ''}`}>
                      {person.totalProfit >= 0 ? '' : '-'}${Math.abs(Math.round(person.totalProfit)).toLocaleString()}
                    </span>
                    <span className="dash-lb-margin">{Math.round(person.margin)}% margin</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="dash-leaderboard-footer">
            <span>Profit = (Route Revenue − Labor − Drive Time) ÷ Team Size</span>
          </div>
        </div>
      )}

      {/* Review Alerts */}
      <ReviewAlerts />

      {/* Complaints & Feedback */}
      {(recentComplaints.length > 0 || recentFeedbacks.length > 0) && (
        <div className="dash-two-col">
          {/* Recent Complaints */}
          <div className="dash-card">
            <div className="dash-card-header">
              <h2><AlertTriangle size={16} style={{ color: openComplaintCount > 0 ? '#ef4444' : 'var(--text-muted)' }} /> Complaints {openComplaintCount > 0 && <span className="dash-complaint-badge">{openComplaintCount} open</span>}</h2>
              <Link to="/admin/complaints-feedback" className="dash-view-all">View All <ArrowRight size={14} /></Link>
            </div>
            <div className="dash-cf-list">
              {recentComplaints.length === 0 && <div className="dash-empty">No complaints recorded</div>}
              {recentComplaints.map(c => {
                const SourceIcon = sourceIcons[c.source] || MessageCircle;
                return (
                  <Link key={c.id} to="/admin/complaints-feedback" className={`dash-cf-row complaint ${c.status}`}>
                    <div className="dash-cf-icon complaint">
                      <AlertTriangle size={14} />
                    </div>
                    <div className="dash-cf-info">
                      <div className="dash-cf-name-row">
                        <span className="dash-cf-name">{getComplaintClientName(c.clientId)}</span>
                        <span className="dash-cf-source"><SourceIcon size={11} /></span>
                      </div>
                      <span className="dash-cf-desc">{c.description?.length > 80 ? c.description.slice(0, 80) + '...' : c.description}</span>
                      {c.employeeIds?.length > 0 && (
                        <span className="dash-cf-employees">{getComplaintEmployeeNames(c.employeeIds)}</span>
                      )}
                    </div>
                    <div className="dash-cf-meta">
                      <span className={`dash-cf-status ${c.status}`}>
                        {c.status === 'open' ? 'Open' : c.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                      </span>
                      <span className="dash-cf-date">{c.date ? format(parseISO(c.date), 'MMM d') : ''}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Recent Positive Feedback */}
          <div className="dash-card">
            <div className="dash-card-header">
              <h2><ThumbsUp size={16} style={{ color: '#10b981' }} /> Positive Feedback</h2>
              <Link to="/admin/complaints-feedback" className="dash-view-all">View All <ArrowRight size={14} /></Link>
            </div>
            <div className="dash-cf-list">
              {recentFeedbacks.length === 0 && <div className="dash-empty">No feedback recorded yet</div>}
              {recentFeedbacks.map(f => {
                const SourceIcon = sourceIcons[f.source] || MessageCircle;
                return (
                  <Link key={f.id} to="/admin/complaints-feedback" className={`dash-cf-row feedback ${f.acknowledged ? 'acknowledged' : ''}`}>
                    <div className="dash-cf-icon feedback">
                      <Star size={14} />
                    </div>
                    <div className="dash-cf-info">
                      <div className="dash-cf-name-row">
                        <span className="dash-cf-name">{getComplaintClientName(f.clientId)}</span>
                        <span className="dash-cf-source"><SourceIcon size={11} /></span>
                        {f.acknowledged && <span className="dash-cf-ack"><CheckCircle size={11} /> Ack</span>}
                      </div>
                      <span className="dash-cf-desc">{f.description?.length > 80 ? f.description.slice(0, 80) + '...' : f.description}</span>
                      {f.employeeIds?.length > 0 && (
                        <span className="dash-cf-employees">{getComplaintEmployeeNames(f.employeeIds)}</span>
                      )}
                    </div>
                    <div className="dash-cf-meta">
                      <span className="dash-cf-date">{f.date ? format(parseISO(f.date), 'MMM d') : ''}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Three columns */}
      <div className="dash-three-col">
        <div className="dash-card">
          <div className="dash-card-header"><h2>Weekly Revenue</h2></div>
          <SparkBar data={weeklyRevenue} height={110} />
          <div className="dash-revenue-footer">
            <div>
              <div className="dash-revenue-footer-label">MTD Total</div>
              <div className="dash-revenue-footer-value sage">{formatCurrency(revenueStats.paid).replace('.00', '')}</div>
            </div>
            <div className="dash-revenue-footer-right">
              <div className="dash-revenue-footer-label">vs Last Month</div>
              <div className={`dash-revenue-footer-value ${revenueStats.mom >= 0 ? 'sage' : 'red'}`}>{revenueStats.mom >= 0 ? '+' : ''}{revenueStats.mom.toFixed(1)}%</div>
            </div>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <h2>Nearby Clients</h2>
            <span className="dash-card-subtitle">Address lookup</span>
          </div>
          <ProximityLookup properties={properties} clients={clients} jobs={jobs} />
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <h2>Recent Invoices</h2>
            <Link to="/admin/invoices" className="dash-view-all">View All <ArrowRight size={14} /></Link>
          </div>
          <div className="dash-invoice-list">
            {recentInvoices.length === 0 && <div className="dash-empty">No invoices yet</div>}
            {recentInvoices.map(inv => {
              const st = statusStyle(inv.status);
              return (
                <Link key={inv.id} to={`/admin/invoices/${inv.id}`} className="dash-invoice-row">
                  <div className="dash-invoice-info">
                    <div className="dash-invoice-name">{inv.clientName || 'Unknown'}</div>
                    <div className="dash-invoice-number">{inv.invoiceNumber || inv.id}</div>
                  </div>
                  <div className="dash-invoice-amount">{formatCurrency(inv.total || 0).replace('.00', '')}</div>
                  <span className="dash-status-badge sm" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="dash-bottom-row">
        <div className="dash-card">
          <h2 className="dash-card-title">Client Mix</h2>
          {clientMix.mix.map(seg => (
            <div key={seg.type} className="dash-mix-item">
              <div className="dash-mix-header">
                <span className="dash-mix-label">{seg.label}</span>
                <span className="dash-mix-stats">{seg.count} clients · {seg.pct}%</span>
              </div>
              <div className="dash-mix-bar-track">
                <div className={`dash-mix-bar-fill ${seg.type}`} style={{ width: `${seg.pct}%` }} />
              </div>
            </div>
          ))}
          <div className="dash-mix-total">
            <div className="dash-mix-total-label">Est. Annual Recurring</div>
            <div className="dash-mix-total-value">{formatCurrency(clientMix.totalAnnual).replace('.00', '')}</div>
          </div>
        </div>

        <div className="dash-card dash-quick-actions">
          <div>
            <h2 className="dash-card-title light">Quick Actions</h2>
            <p className="dash-quick-actions-sub">Jump to common workflows</p>
          </div>
          <div className="dash-quick-grid">
            {quickActions.map(a => a.action ? (
              <button key={a.label} onClick={a.action} className="dash-quick-tile">
                <div className="dash-quick-icon">{a.icon}</div>
                <div className="dash-quick-label">{a.label}</div>
                <div className="dash-quick-desc">{a.desc}</div>
              </button>
            ) : (
              <Link key={a.label} to={a.path} className="dash-quick-tile">
                <div className="dash-quick-icon">{a.icon}</div>
                <div className="dash-quick-label">{a.label}</div>
                <div className="dash-quick-desc">{a.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
